import { globalStores } from '@/editor/stores';
import {
  IBaseDataArcEdgeFace,
  IBaseDataContourFace,
  IBaseDataCylinderFace,
  IBaseDataEdgeFace
} from '@/editor/data/face/face';
import { pixelRatio3d } from '@/editor/math/const';
import BaseModel3d from '../BaseModel3d';
import { LineSegments2, LineSegmentsGeometry, LineMaterial } from 'three/examples/jsm/Addons.js';
import { setModelRenderOrder } from '../../utils';
import { Color } from 'three';
import { getCircleContour } from '@/editor/utils/face';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';

export class FaceOutlineModel extends BaseModel3d {
  private _color: number = 0x00000;
  private _renderOrder: number = 0;
  private _material = new LineMaterial({
    color: this._color,
    linewidth: 1,
    depthTest: false,
    depthWrite: false
  });

  constructor() {
    super({ id: 'FaceBoundingBox' });

    globalStores.editorStateStore.sceneInfo.scene3d.scene.add(this.model);
  }

  setColor(color: number) {
    this._color = color;
    this._material.color = new Color(color);
  }

  setRenderOrder(order: number) {
    this._renderOrder = order;
  }

  createEdgeModel(baseData: IBaseDataEdgeFace) {
    const { start, end, height, offGround } = baseData;

    const points = [
      { x: start.x, y: start.y, z: offGround },
      { x: end.x, y: end.y, z: offGround },
      { x: end.x, y: end.y, z: offGround + height },
      { x: start.x, y: start.y, z: offGround + height }
    ];

    this.createOutline(points);
  }

  createContourModel(baseData: IBaseDataContourFace) {
    const { contour, offGround } = baseData;

    const points = [];
    for (let i = 0; i < contour.length; i++) {
      const { x, y, center, clockwise } = contour[i];

      if (center && clockwise !== undefined) {
        const nextPoint = contour[(i + 1) % contour.length];
        const arcSeg = new ArcSeg2d({ start: { x, y }, end: nextPoint, center, clockwise });
        const arcPoints = arcSeg.getPoints();
        points.push(...arcPoints.map((point) => ({ x: point.x, y: point.y, z: offGround })));
      } else {
        points.push({ x, y, z: offGround });
      }
    }

    this.createOutline(points);
  }

  createCylinderModel(baseData: IBaseDataCylinderFace) {
    const { center, radius, height, offGround } = baseData;
    const points = [];

    const circlePoints = getCircleContour(center, radius, 64);
    circlePoints.push(circlePoints[0]);
    points.push(
      ...circlePoints.map((point) => ({ x: point.x, y: point.y, z: offGround })),
      ...circlePoints.map((point) => ({ x: point.x, y: point.y, z: offGround + height }))
    );

    this.createOutline(points);
  }

  createOutline(points: { x: number; y: number; z: number }[]) {
    const geometry = new LineSegmentsGeometry();
    const positions: number[] = [];
    for (let i = 0; i < points.length; i++) {
      const start = points[i];
      const end = points[(i + 1) % points.length];
      positions.push(start.x / pixelRatio3d, start.y / pixelRatio3d, start.z / pixelRatio3d);
      positions.push(end.x / pixelRatio3d, end.y / pixelRatio3d, end.z / pixelRatio3d);
    }

    geometry.setPositions(positions);
    const line = new LineSegments2(geometry, this._material);
    this.model.add(line);

    setModelRenderOrder(this.model, this._renderOrder);
  }

  createArcEdgeModel(baseData: IBaseDataArcEdgeFace) {
    const { start, end, center, clockwise, height, offGround } = baseData;

    const arcSeg = new ArcSeg2d({ start, end, center, clockwise });

    const arcPoints = arcSeg.getPoints();
    const points = [
      ...arcPoints.map((point) => ({ x: point.x, y: point.y, z: offGround })),
      ...arcPoints.map((point) => ({ x: point.x, y: point.y, z: offGround + height })).reverse()
    ];

    this.createOutline(points);
  }
}
